import { useRef } from 'react';
import { isActionColumn } from './ActionColumn.js';
import { isContent } from './Content.js';
import { DataCell } from './DataColumn.js';
import styles from './Table.module.scss.js';
import { xor } from './util.js';
import { jsx } from 'react/jsx-runtime';
import { useThemeVariable } from '../util/useThemeVariable.js';
import classNames from 'clsx';
import { isAffirmativeKeypress } from '../util/aria.js';

function getIndentPadding(row) {
  const parentRow = row.getParentRow();
  let level = 0;
  let expand = 0;
  let select = 0;

  // a row at the root level should not be indented.
  if (!parentRow) {
    return '0';
  }
  const parentDepth = parentRow.depth;
  level = row.depth;

  // expandable content should be indented to the text portion of the parent row.
  if (isContent(row.original)) {
    level = parentDepth;
    expand = parentRow.getCanExpand() ? 1 : 0;
    select = parentRow.getCanSelect() ? 1 : 0;
  }
  return `calc((${level} * 2rem) + (${expand} * (2.25rem)) + (${select} * (2.25rem)))`;
}
function TableRow(props) {
  const {
    row
  } = props;
  return isContent(row.original) ? /*#__PURE__*/jsx(ContentRow, {
    ...props
  }) : /*#__PURE__*/jsx(DataRow, {
    ...props
  });
}
function DataRow(props) {
  const {
    row,
    tableMeta,
    side
  } = props;
  const {
    primaryRowAction
  } = getRowHandlers(row, tableMeta);
  const {
    rowColor
  } = tableMeta;
  const canFocus = !!primaryRowAction;
  const selected = row.getIsSelected();
  const className = classNames(styles['data-row'], selected && styles['selected']);
  const ref = useRef(null);
  const color = rowColor?.(row.original);
  const backgroundColor = useThemeVariable(color ?? '', ref);
  const cells = side === 'center' ? row.getCenterVisibleCells() : row.getLeftVisibleCells();
  const tds = cells.map(c => /*#__PURE__*/jsx(DataCell, {
    cell: c
  }, c.id));
  if (canFocus) {
    return /*#__PURE__*/jsx("tr", {
      "data-dsx--table--row": row.id,
      ref: ref,
      style: {
        backgroundColor
      },
      className: className,
      tabIndex: 0,
      onClick: e => shouldTriggerPrimary(e) && primaryRowAction(row.original),
      onKeyDown: isAffirmativeKeypress(e => shouldTriggerPrimary(e) && primaryRowAction(row.original)),
      children: tds
    });
  }
  return /*#__PURE__*/jsx("tr", {
    "data-dsx--table--row": row.id,
    className: className,
    ref: ref,
    style: {
      backgroundColor
    },
    children: tds
  });
}
function shouldTriggerPrimary(e) {
  // clicking on a "non-interactive element" should return false
  const target = e.target;
  const isOwnInput = target.dataset.tableInternalInput === 'true';
  return isOwnInput || !isInputy(target);
}
function isInputy(target) {
  if (!target) {
    return false;
  }
  const tagName = target.tagName.toLowerCase();
  const role = target.role?.toLowerCase() ?? '';
  const hasInputTrait = ['input', 'select', 'option', 'textarea', 'button'].includes(tagName) || ['menu', 'menuitem'].includes(role);
  return hasInputTrait || isInputy(target.parentElement);
}
function ContentRow(props) {
  const {
    row
  } = props;
  const original = row.original;
  const paddingLeft = getIndentPadding(row);
  const cellStyle = {
    paddingLeft
  };
  return /*#__PURE__*/jsx("tr", {
    className: styles['content-row'],
    children: /*#__PURE__*/jsx("td", {
      style: cellStyle,
      colSpan: row.getVisibleCells().length,
      children: original.content
    })
  });
}
function getRowHandlers(row, tableMeta) {
  const expandVisible = row.getCanExpand();
  const selectVisible = row.getCanSelect();
  const {
    disableExpand,
    disableSelect
  } = tableMeta;
  const cells = row.getVisibleCells();
  const actionCol = cells.find(x => isActionColumn(x.column.columnDef.meta))?.column.columnDef.meta;
  const handleAction = actionCol?.onAction;
  const expandDisabled = disableExpand?.(row.original);
  const selectDisabled = disableSelect?.(row.original);
  const handleExpand = expandVisible ? row.getToggleExpandedHandler() : undefined;
  const handleSelect = selectVisible ? row.getToggleSelectedHandler() : undefined;
  const hasPrimaryRowAction = xor(!!handleExpand, !!handleSelect, !!handleAction);
  const primaryRowAction = hasPrimaryRowAction ? handleExpand ?? handleSelect ?? handleAction : undefined;
  return {
    expandVisible,
    expandDisabled,
    handleExpand,
    selectVisible,
    selectDisabled,
    handleSelect,
    primaryRowAction
  };
}

export { TableRow, getIndentPadding, getRowHandlers };
